/**
 * WeeklyReportSection — weekly status report form matching the CST board format
 * described by Andrew Peterson.
 *
 * Form fields:
 *   - Date range: week_start (Start Date) → week_ending (End Date)
 *   - Escalation: green / red only (no yellow)
 *   - Schedule / Scope / Budget: green / yellow / red
 *   - Cumulative Hours: total hours logged to date (validated — see weekly-report-schema.ts)
 *   - Phase completion dates: one per CASDM phase, only log when actually completed
 *   - Current Phase
 *   - Project Health (text)
 *   - Planned vs Actual / Slippages (text)
 *   - What the team did this week (text)
 *   - What the team will do next (text)
 *   - Risks (text)
 *   - Decisions (text)
 *
 * Validation: React Hook Form + Zod (`weekly-report-schema.ts`), mirroring the server contract in
 *             packages/weekly-reports/validation.ts. Errors render inline under each field.
 *
 * Typography: section headings / sub headings / field titles use the named design-system
 *             typography tokens (`text-section-heading` 18px semibold, `text-sub-heading` 16px
 *             semibold, `text-field-title` 14px medium — see src/styles/globals.css @theme).
 *             Never inline px, and never stack `font-bold`/`font-medium` on top of a token: the
 *             generated utility resolves weight via `var(--tw-font-weight, …)` and would lose.
 *
 * Status indicators: the overall-status badge is wrapped in the DS `Tooltip` (hover + focus) and
 *             carries an `aria-label`, so the green/yellow/red colour is never the sole carrier of
 *             meaning. Copy: green → "On Track", yellow → "At Risk", red → "Off Track /
 *             Escalated" (`weekly-report-messages.ts` → `overallStatus`).
 *
 * Copy: all user-facing strings come from `weekly-report-messages.ts` — none are inlined in JSX.
 *
 * Display: Overview mode shows the most recent SUBMITTED report as a snapshot. Full mode (T-27)
 *          shows one list of every visible report (submitted + the viewer's drafts), newest week
 *          first, filterable by review status. Row content lives in `WeeklyReportListRow.tsx`.
 *
 * Create flow: the form opens in a right-hand slide-in side panel (DS `Sheet`), not a centred
 *          modal — see `CreateReportPanel`.
 *
 * Access: PM and Leadership can submit. SA sees read-only. Server enforces PM-own visibility.
 *
 * Drafts + edit (V047):
 *   - "Save as Draft" persists an incomplete report — only the end date is required
 *     (`weeklyReportDraftSchema`); "Submit report" runs the full schema.
 *   - The viewer's drafts appear in the report list with a "Draft" chip and "Continue editing".
 *   - Submitted reports show "Edit" to their author or leadership/admin; the same panel opens
 *     pre-filled (`reportToFormValues`) and re-submits via PUT. Submitted → draft is not offered.
 *   - The snapshot and prior-phase carry-forward read SUBMITTED reports only.
 *   - Phase dates SAVED on the report being edited are protected: lowering Current Phase hides
 *     the row but keeps the value in the payload, with a role="status" notice. The API mirrors
 *     this (COALESCE on update unless `clear_phase_dates` names the phase). There is no UI to
 *     clear a saved phase date yet — blanking an editable saved date is likewise a no-op server-side.
 */
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { CalendarDays, Loader2, TrendingUp } from "lucide-react";

import { isAxiosError } from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  isDraftReport,
  matchesReportListFilter,
  submittedReportsOnly,
  useSubmitWeeklyReport,
  useUpdateWeeklyReport,
  useWeeklyReports,
  type WeeklyReportListFilter,
} from "@/hooks/useWeeklyReports";
import { useProject } from "@/hooks/useProjects";
import {
  canActAsMember,
  isMembershipBypass,
  toastIfNotProjectMember,
} from "@/lib/project-permissions";
import type {
  CasdmPhase,
  CreateWeeklyReportInput,
  EscalationStatus,
  Project,
  RagStatus,
  WeeklyReport,
  WeeklyReportLifecycleStatus,
  WeeklyReportStatus,
} from "@/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { FieldLabel } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Select } from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { weeklyReportMessages as msg } from "./weekly-report-messages";
import {
  ReviewStatusBadge,
  WeeklyReportReview,
  canReviewReports,
} from "./WeeklyReportReview";
import {
  ESCALATION_META,
  OVERALL_STATUS_META,
  OverallStatusBadge,
  RAG_META,
  RagDot,
  WeeklyReportRowSummary,
  fmtDate,
  weeklyReportListMessages as listMsg,
} from "./WeeklyReportListRow";
import {
  reportToFormValues,
  weeklyReportDraftSchema,
  weeklyReportFormDefaults,
  weeklyReportFormSchema,
  type WeeklyReportFormValues,
} from "./weekly-report-schema";
import { phaseLabel, phaseNumber } from "@/lib/phases";

// ─── Constants ───────────────────────────────────────────────────────────────
// OVERALL_STATUS_META / ESCALATION_META / RAG_META, OverallStatusBadge and RagDot live in
// WeeklyReportListRow.tsx (T-27) so the list row and this section share one definition.

/**
 * `label` is what the form saves to `weekly_reports.current_phase` (free text): the canonical
 * "Phase N — <name>" from lib/phases. Reports saved before this used "P0 — Internal Prep" … — see
 * phaseKeyFromLabel() and reportToFormValues() for how those are still read.
 */
const CASDM_PHASES: Array<{ key: CasdmPhase; label: string }> = (
  ["p0", "p1", "p2", "p3", "p4"] as const
).map((key, n) => ({ key, label: phaseLabel(n) }));

/** Form field name for a phase's completion date. */
type PhaseField = `phase_${CasdmPhase}_completed_at`;

/** Zero-based position of a phase in the CASDM sequence. */
const PHASE_INDEX: Record<CasdmPhase, number> = {
  p0: 0,
  p1: 1,
  p2: 2,
  p3: 3,
  p4: 4,
};

// ─── Progressive phase disclosure (Task 5) ───────────────────────────────────

/**
 * The most recently recorded completion date per phase, taken from this project's PRIOR weekly
 * reports.
 *
 * Why this is needed at all: each `weekly_reports` row carries its own full set of
 * `phase_pX_completed_at` columns, and the snapshot UI reads only the most recent row. So a new
 * report that omits a phase date does not "inherit" it — it erases it from the visible snapshot.
 * Prior values are therefore both displayed as read-only AND carried forward into the submitted
 * payload.
 *
 * `reports` arrives ordered `week_ending DESC` from the API, so the first non-null value found for
 * a phase is the latest recorded one.
 */
export function derivePriorPhaseCompletions(
  reports: readonly WeeklyReport[] | undefined,
): Partial<Record<CasdmPhase, string>> {
  const result: Partial<Record<CasdmPhase, string>> = {};
  // Drafts are unpublished (V047) — a date typed into a draft is not yet a recorded completion,
  // so it must never be locked read-only or carried forward into another report.
  for (const report of submittedReportsOnly(reports)) {
    for (const phase of CASDM_PHASES) {
      if (result[phase.key]) continue;
      const value = report[
        `phase_${phase.key}_completed_at` as keyof WeeklyReport
      ] as string | null | undefined;
      if (value) result[phase.key] = value.slice(0, 10);
    }
  }
  return result;
}

/**
 * The phase completion dates already saved on `report` itself (YYYY-MM-DD), or `{}` for a new
 * report. Used to protect the edited report's own history from being blanked when Current Phase
 * is lowered — see the phase-sync effect in `SubmitReportForm`.
 */
export function persistedPhaseDates(
  report: WeeklyReport | undefined,
): Partial<Record<CasdmPhase, string>> {
  const result: Partial<Record<CasdmPhase, string>> = {};
  if (!report) return result;
  for (const phase of CASDM_PHASES) {
    const value = report[
      `phase_${phase.key}_completed_at` as keyof WeeklyReport
    ] as string | null | undefined;
    if (value) result[phase.key] = value.slice(0, 10);
  }
  return result;
}

/**
 * "2026-08-01" → "Aug 1, 2026" in the viewer's locale. A bare YYYY-MM-DD is parsed as a LOCAL
 * calendar date: `new Date("2026-08-01")` is UTC midnight and shows the previous day west of UTC.
 */
function fmtCalendarDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const date = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** How a phase row renders in the completion-dates section. */
export type PhaseDisclosure = "hidden" | "readonly" | "editable";

/**
 * Decide how each phase row renders, given the selected current phase and what prior reports
 * already recorded.
 *
 *   • No current phase selected  → every row hidden. The current phase is the axis the whole
 *                                  section is organised around; showing five inputs before it is
 *                                  chosen is the behaviour this task replaces.
 *   • Future phase (beyond current) → hidden. A completion date for a phase the project has not
 *                                  reached is not a late entry, it is a wrong entry.
 *   • Current phase              → editable. Exactly one input, which is the point.
 *   • Past phase                 → read-only, ALWAYS. Its recorded date is shown when one exists
 *                                  (a prior report, this report's own saved value, or a date typed
 *                                  in this session while that phase was current); otherwise
 *                                  "Not recorded".
 *
 * "Only the current phase is editable" is applied literally. An earlier version left an UNRECORDED
 * past phase editable to avoid a dead end (a first-ever report filed at P2 could never record P0/P1),
 * but that put up to five editable date inputs on the form and let a later week back-fill history
 * with no audit trail — reported as a defect. The trade-off is accepted: an unrecorded past phase
 * stays "Not recorded"; correcting it is a data fix, not a weekly-report edit.
 *
 * `priorCompletions` is kept in the signature for call-site stability; the decision no longer
 * depends on it (it still drives which recorded value is displayed).
 */
export function phaseDisclosure(
  phase: CasdmPhase,
  currentPhase: CasdmPhase | null,
  _priorCompletions?: Partial<Record<CasdmPhase, string>>,
): PhaseDisclosure {
  if (!currentPhase) return "hidden";
  if (PHASE_INDEX[phase] > PHASE_INDEX[currentPhase]) return "hidden";
  if (phase === currentPhase) return "editable";
  return "readonly";
}

/**
 * Map a stored `current_phase` label back to its CasdmPhase key. Accepts the canonical label
 * ("Phase 2 — Design & Review"), the legacy weekly-report labels ("P2 — Design & Review",
 * "P0 — Internal Prep") and a bare "Phase 2".
 */
export function phaseKeyFromLabel(
  label: string | null | undefined,
): CasdmPhase | null {
  const n = phaseNumber(label);
  return n === null ? null : (CASDM_PHASES[n]?.key ?? null);
}

// ─── Small helpers ────────────────────────────────────────────────────────────

/** `''` → undefined, otherwise the trimmed value. Keeps optional API fields off the payload. */
function optional(value: string | undefined): string | undefined {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? undefined : trimmed;
}

/** Email equality, normalised like the server (`lower(btrim(...))`). */
function sameUser(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** The subset of the signed-in user this section needs. */
interface ReportViewer {
  role?: string;
  email?: string;
}

/** Roles allowed to create / edit weekly reports (server: create + update handlers). */
const REPORT_WRITE_ROLES = ["pm", "leadership", "admin"];

/**
 * Client-side mirror of the server's edit rules (packages/weekly-reports/services/
 * weekly-report.service.ts `decideEdit`). UX only — the API enforces them.
 *   - the viewer must be on the project's team (`viewer_is_member`); leadership/admin bypass.
 *     `project` undefined (still loading) → false.
 *   - draft     → its author only
 *   - submitted → its author, or leadership/admin
 */
export function canEditReport(
  report: Pick<WeeklyReport, "status" | "submitted_by">,
  viewer: ReportViewer | null | undefined,
  project: Pick<Project, "viewer_is_member"> | null | undefined,
): boolean {
  if (!viewer?.role) return false;
  if (!canActAsMember(viewer.role, REPORT_WRITE_ROLES, project)) return false;
  const isAuthor = sameUser(report.submitted_by, viewer.email);
  if (isDraftReport(report)) return isAuthor;
  return isAuthor || isMembershipBypass(viewer.role);
}

/** Build the API payload from form values. Blank optional fields are left off. */
function toPayload(
  values: Partial<WeeklyReportFormValues> & { week_ending: string },
  status: WeeklyReportLifecycleStatus,
): CreateWeeklyReportInput {
  return {
    status,
    week_start: optional(values.week_start),
    week_ending: values.week_ending,
    overall_status: values.overall_status,
    escalation: optional(values.escalation) as EscalationStatus | undefined,
    schedule: optional(values.schedule) as RagStatus | undefined,
    scope: optional(values.scope) as RagStatus | undefined,
    budget_status: optional(values.budget_status) as RagStatus | undefined,
    cumulative_hours: optional(values.cumulative_hours)
      ? Number(values.cumulative_hours)
      : undefined,
    phase_p0_completed_at: optional(values.phase_p0_completed_at),
    phase_p1_completed_at: optional(values.phase_p1_completed_at),
    phase_p2_completed_at: optional(values.phase_p2_completed_at),
    phase_p3_completed_at: optional(values.phase_p3_completed_at),
    phase_p4_completed_at: optional(values.phase_p4_completed_at),
    current_phase: optional(values.current_phase),
    project_health: optional(values.project_health),
    planned_vs_actual: optional(values.planned_vs_actual),
    team_did_this_week: optional(values.team_did_this_week),
    team_will_do_next: optional(values.team_will_do_next),
    risks: optional(values.risks),
    decisions: optional(values.decisions),
  };
}

/** Prefer the API's `{ message }` (e.g. the 409 conflict copy) over axios' generic text. */
function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError<{ message?: string }>(err)) {
    return err.response?.data?.message ?? fallback;
  }
  return err instanceof Error ? err.message : fallback;
}

// ─── RAG selector ─────────────────────────────────────────────────────────────

function RagSelect({
  id,
  label,
  value,
  onChange,
  escalationOnly,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  escalationOnly?: boolean;
}): JSX.Element {
  const options = escalationOnly
    ? (["green", "red"] as const)
    : (["green", "yellow", "red"] as const);

  return (
    <div className="space-y-1.5">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select
        id={id}
        aria-label={label}
        value={value}
        onChange={onChange}
        placeholder={msg.placeholders.select}
        options={options.map((opt) => {
          const meta = escalationOnly
            ? ESCALATION_META[opt as EscalationStatus]
            : RAG_META[opt as RagStatus];
          return {
            value: opt,
            label: (
              <span className="flex items-center gap-2">
                <span
                  className={`h-2.5 w-2.5 rounded-full ${opt === "green" ? "bg-green-500" : opt === "yellow" ? "bg-yellow-400" : "bg-red-500"}`}
                />
                {meta.label}
              </span>
            ),
          };
        })}
      />
    </div>
  );
}

// ─── Submit form ──────────────────────────────────────────────────────────────

function SubmitReportForm({
  projectId,
  onSuccess,
  report,
}: {
  projectId: string;
  onSuccess?: () => void;
  /**
   * V047 — an existing report to edit. Omitted → create. A draft opens with "Save as Draft" +
   * "Submit"; a submitted report opens with "Re-submit" only (no submitted → draft).
   */
  report?: WeeklyReport;
}): JSX.Element {
  const submit = useSubmitWeeklyReport(projectId);
  const update = useUpdateWeeklyReport(projectId);
  const editingSubmitted = !!report && !isDraftReport(report);
  const isPending = submit.isPending || update.isPending;

  // Prior reports for this project. Served from the same react-query cache key the surrounding
  // section already populated, so opening the panel costs no extra request. The report being
  // edited is excluded: its own dates are already in the form and must stay editable.
  const { data: priorReports } = useWeeklyReports(projectId);
  const priorCompletions = useMemo(
    () =>
      derivePriorPhaseCompletions(
        (priorReports ?? []).filter((r) => r.id !== report?.id),
      ),
    [priorReports, report?.id],
  );

  // The phase dates already SAVED on the report being edited (empty for a new report). These are
  // protected: lowering Current Phase must never blank them, because the PUT is a full-row write
  // and a blank would permanently erase this report's own history with no warning. The API
  // enforces the same rule (COALESCE unless `clear_phase_dates` lists the phase).
  const ownPersisted = useMemo(() => persistedPhaseDates(report), [report]);

  // Value shown/forced for a read-only row: the report's OWN saved date wins over a date carried
  // forward from another report, so opening an older report never rewrites its own record.
  const recordedCompletions = useMemo(
    () => ({ ...priorCompletions, ...ownPersisted }),
    [priorCompletions, ownPersisted],
  );

  // T-30 — the project's all-time Clockify hours. Same react-query key ("project", id) the project
  // page already loaded, so this costs no extra request.
  const { data: project } = useProject(projectId) as { data?: Project };

  const {
    clearErrors,
    control,
    formState: { errors },
    getFieldState,
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
  } = useForm<WeeklyReportFormValues>({
    resolver: zodResolver(weeklyReportFormSchema),
    // Existing report → every saved field pre-filled (map report → form values).
    defaultValues: report
      ? reportToFormValues(report)
      : weeklyReportFormDefaults,
    // Re-validate as the user types so a rejected value clears as soon as it becomes valid.
    mode: "onChange",
  });

  const currentPhase = phaseKeyFromLabel(watch("current_phase"));

  // T-30 — pre-fill Cumulative Hours from Clockify on a NEW report only. Never for an existing
  // report (its saved value — even a blank one — is the record), and never over anything the user
  // has typed: the effect bails if the field is dirty or non-blank, and `project` usually arrives
  // from cache before first paint anyway. The value stays fully editable.
  // Only a value the daily sync wrote (synced_at set) may pre-fill; anything else is not "from Clockify".
  const clockifyHours =
    report || !project?.clockify_hours_synced_at
      ? null
      : (project?.clockify_hours_consumed ?? null);
  const [clockifyPrefill, setClockifyPrefill] = useState<string | null>(null);
  useEffect(() => {
    if (clockifyHours == null || clockifyPrefill !== null) return;
    if (getFieldState("cumulative_hours").isDirty) return;
    if ((getValues("cumulative_hours") ?? "").trim() !== "") return;
    const value = String(clockifyHours);
    setValue("cumulative_hours", value, {
      shouldDirty: false,
      shouldValidate: true,
    });
    setClockifyPrefill(value);
  }, [clockifyHours, clockifyPrefill, getFieldState, getValues, setValue]);
  // The helper is only true while the field still holds the Clockify value.
  const showClockifyHint =
    clockifyPrefill !== null && watch("cumulative_hours") === clockifyPrefill;
  const clockifyHintId = "cumulative-hours-clockify-hint";

  // Keep the five phase-date fields in sync with the selected current phase and with what prior
  // reports already recorded.
  //
  // Deliberately ONE effect rather than two (seed, then clear-future). Two effects raced: the seed
  // ran on mount while no current phase was selected yet, so every phase was still classified
  // `hidden` and the clear pass immediately wiped the values the seed had just written. The prior
  // dates then never reached the payload, silently erasing them from the snapshot — which reads
  // only the most recent report row.
  //
  // Per phase:
  //   hidden   → force blank. The row is not rendered, so a stale value would be invisible AND
  //              unremovable by the user, and would submit a completion date for a phase the
  //              project has not reached.
  //   readonly → force the prior recorded value. It is displayed read-only but must still be
  //              submitted, because each weekly_reports row carries its own full set of columns.
  //   editable → seed from the prior value only when the field is still blank, so typing is never
  //              clobbered on re-render.
  //
  // Editing an existing report adds one exception to `hidden`: a date SAVED on this report is
  // restored and kept (never blanked) — only values typed in this session are cleared. The kept
  // date is announced by an inline role="status" notice (see `keptPhaseDates` below).
  useEffect(() => {
    const opts = { shouldDirty: false, shouldValidate: false } as const;
    for (const phase of CASDM_PHASES) {
      const field = `phase_${phase.key}_completed_at` as PhaseField;
      const prior = priorCompletions[phase.key];
      const recorded = recordedCompletions[phase.key];
      const own = ownPersisted[phase.key];

      switch (phaseDisclosure(phase.key, currentPhase, priorCompletions)) {
        case "hidden":
          if (own) {
            if (getValues(field) !== own) setValue(field, own, opts);
          } else if (getValues(field)) {
            setValue(field, "", opts);
          }
          break;
        case "readonly":
          // A recorded date wins; with none, keep whatever this session holds (e.g. a date typed
          // while this phase was still current, before the user advanced Current Phase).
          if (recorded && getValues(field) !== recorded)
            setValue(field, recorded, opts);
          break;
        case "editable":
          if (prior && !getValues(field)) setValue(field, prior, opts);
          break;
      }
    }
  }, [
    currentPhase,
    priorCompletions,
    recordedCompletions,
    ownPersisted,
    getValues,
    setValue,
  ]);

  // Saved-on-this-report dates whose rows are hidden by the current selection — kept in the
  // payload and announced to the user rather than silently dropped.
  const keptPhaseDates = CASDM_PHASES.filter(
    (phase) =>
      !!ownPersisted[phase.key] &&
      phaseDisclosure(phase.key, currentPhase, priorCompletions) === "hidden",
  );

  /** POST for a new report, PUT for an existing one. */
  const persist = (
    input: CreateWeeklyReportInput,
    successMessage: string,
    failureMessage: string,
  ): void => {
    const handlers = {
      onSuccess: () => {
        toast.success(successMessage);
        reset(weeklyReportFormDefaults);
        onSuccess?.();
      },
      onError: (err: unknown) => {
        if (toastIfNotProjectMember(err)) return;
        toast.error(errorMessage(err, failureMessage));
      },
    };
    if (report) {
      update.mutate({ reportId: report.id, input }, handlers);
    } else {
      submit.mutate(input, handlers);
    }
  };

  // "Submit" / "Re-submit" — full schema via the RHF resolver.
  const onSubmit = (values: WeeklyReportFormValues): void => {
    persist(
      toPayload(values, "submitted"),
      editingSubmitted ? msg.status.resubmitSuccess : msg.status.submitSuccess,
      msg.status.submitFailure,
    );
  };

  // "Save as Draft" — bypasses the full resolver: only the end date is required, but field
  // formats (cumulative hours) are still enforced so the server never rejects the draft.
  const onSaveDraft = (): void => {
    clearErrors();
    const parsed = weeklyReportDraftSchema.safeParse(getValues());
    if (!parsed.success) {
      // First issue per field wins — same as the resolver's default `criteriaMode: 'firstError'`,
      // so the draft path shows the same message the live validation does.
      const seen = new Set<string>();
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof WeeklyReportFormValues | undefined;
        if (!field || seen.has(field)) continue;
        seen.add(field);
        setError(field, { type: "draft", message: issue.message });
      }
      return;
    }
    persist(
      toPayload(parsed.data, "draft"),
      msg.status.draftSaved,
      msg.status.draftFailure,
    );
  };

  return (
    // `@container` makes the form its own size context: every multi-column grid below switches on
    // the FORM's width (`@md:` = 28rem/448px, `@2xl:` = 42rem/672px), not the viewport's. The form
    // lives in a side panel, so on a wide desktop the viewport breakpoints (`lg:`) fired while the
    // panel was still narrow and crammed four RAG selects / three date pickers into ~520px. At the
    // panel's `sm:max-w-3xl` the form is 720px wide, so `@2xl` gives 4 / 3 columns of ≥168px.
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="@container space-y-6"
      data-testid="weekly-report-form"
      noValidate
    >
      {/* ── Date Range ── */}
      <section className="space-y-3">
        <h4 className="text-sub-heading text-text-primary">
          {msg.headings.reportingPeriod}
        </h4>
        <div className="grid gap-4 @md:grid-cols-2">
          <div className="space-y-1.5">
            <FieldLabel htmlFor="week-start">{msg.fields.startDate}</FieldLabel>
            <Controller
              control={control}
              name="week_start"
              render={({ field }) => (
                <DatePicker
                  id="week-start"
                  ref={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </div>
          <div className="space-y-1.5">
            <FieldLabel htmlFor="week-ending">
              {msg.fields.endDate} <span className="text-error">*</span>
            </FieldLabel>
            <Controller
              control={control}
              name="week_ending"
              render={({ field }) => (
                <DatePicker
                  id="week-ending"
                  ref={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  required
                  error={errors.week_ending?.message}
                />
              )}
            />
          </div>
        </div>
      </section>

      <Separator />

      {/* ── RAG Status ── */}
      <section className="space-y-3">
        <h4 className="text-sub-heading text-text-primary">
          {msg.headings.status}
        </h4>
        <div
          className="grid gap-4 @md:grid-cols-2 @2xl:grid-cols-4"
          data-testid="weekly-report-rag-grid"
        >
          {/* Escalation: red/green only */}
          <Controller
            control={control}
            name="escalation"
            render={({ field }) => (
              <RagSelect
                id="escalation"
                label={msg.fields.escalation}
                value={field.value}
                onChange={field.onChange}
                escalationOnly
              />
            )}
          />
          <Controller
            control={control}
            name="schedule"
            render={({ field }) => (
              <RagSelect
                id="schedule"
                label={msg.fields.schedule}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={control}
            name="scope"
            render={({ field }) => (
              <RagSelect
                id="scope"
                label={msg.fields.scope}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={control}
            name="budget_status"
            render={({ field }) => (
              <RagSelect
                id="budget-status"
                label={msg.fields.budget}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <div className="grid gap-4 @md:grid-cols-2">
          <div className="space-y-1.5">
            <FieldLabel htmlFor="overall-status">
              {msg.fields.overallStatus}
            </FieldLabel>
            <Controller
              control={control}
              name="overall_status"
              render={({ field }) => (
                <Select
                  id="overall-status"
                  aria-label={msg.fields.overallStatus}
                  value={field.value}
                  onChange={field.onChange}
                  options={(
                    Object.keys(OVERALL_STATUS_META) as WeeklyReportStatus[]
                  ).map((s) => ({
                    value: s,
                    label: OVERALL_STATUS_META[s].label,
                  }))}
                />
              )}
            />
          </div>
          <div className="space-y-1.5">
            <FieldLabel htmlFor="cumulative-hours">
              {msg.fields.cumulativeHours}
            </FieldLabel>
            <Input
              id="cumulative-hours"
              type="number"
              min="0"
              step="0.5"
              inputMode="decimal"
              placeholder={msg.placeholders.cumulativeHours}
              error={errors.cumulative_hours?.message}
              aria-describedby={
                [
                  errors.cumulative_hours?.message
                    ? "cumulative-hours-error"
                    : null,
                  showClockifyHint ? clockifyHintId : null,
                ]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
              {...register("cumulative_hours")}
            />
            {showClockifyHint && (
              <p
                id={clockifyHintId}
                className="text-xs text-text-secondary"
                data-testid="cumulative-hours-clockify-hint"
              >
                {msg.hints.cumulativeHoursFromClockify(
                  fmtDate(project?.clockify_hours_synced_at),
                )}
              </p>
            )}
          </div>
        </div>
      </section>

      <Separator />

      {/* ── Phase Completion Dates ── */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h4 className="text-sub-heading text-text-primary">
            {msg.headings.phaseCompletion}
          </h4>
          <span className="text-xs text-text-secondary">
            {msg.errors.phaseCompletionHint}
          </span>
        </div>
        <div
          className="grid gap-4 @md:grid-cols-2 @2xl:grid-cols-3"
          data-testid="weekly-report-phase-grid"
        >
          <div className="space-y-1.5">
            <FieldLabel htmlFor="current-phase">
              {msg.fields.currentPhase}
            </FieldLabel>
            <Controller
              control={control}
              name="current_phase"
              render={({ field }) => (
                <Select
                  id="current-phase"
                  aria-label={msg.fields.currentPhase}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={msg.placeholders.selectPhase}
                  options={CASDM_PHASES.map((p) => ({
                    value: p.label,
                    label: p.label,
                  }))}
                />
              )}
            />
          </div>

          {/* Progressive disclosure: the current phase gets the only editable date input;
                  phases already completed in a prior report are shown read-only; phases the
                  project has not reached are not rendered at all. See phaseDisclosure(). */}
          {CASDM_PHASES.map((phase) => {
            const disclosure = phaseDisclosure(
              phase.key,
              currentPhase,
              priorCompletions,
            );
            if (disclosure === "hidden") return null;

            if (disclosure === "readonly") {
              const shown =
                recordedCompletions[phase.key] ||
                watch(`phase_${phase.key}_completed_at` as PhaseField) ||
                "";
              return (
                <div key={phase.key} className="space-y-1.5">
                  <FieldLabel htmlFor={`phase-${phase.key}`}>
                    {phase.label}
                  </FieldLabel>
                  {/* Not a disabled <input>: a disabled control is skipped by some screen
                          readers and reads as "temporarily unavailable" rather than "already
                          recorded". The value is carried in form state (seeded from the prior
                          report) so it is still submitted. */}
                  <p
                    id={`phase-${phase.key}`}
                    data-testid={`phase-${phase.key}-recorded`}
                    className="flex h-[42px] items-center rounded-[var(--radius-input)] border border-outlines bg-subtle-bg px-3.5 text-sm text-text-primary"
                  >
                    {shown ? (
                      <>
                        {fmtCalendarDate(shown)}
                        <span className="ml-2 text-xs text-text-secondary">
                          {msg.fields.phaseAlreadyRecorded}
                        </span>
                      </>
                    ) : (
                      <span className="text-text-secondary">
                        {msg.fields.phaseNotRecorded}
                      </span>
                    )}
                  </p>
                </div>
              );
            }

            return (
              <div key={phase.key} className="space-y-1.5">
                <FieldLabel htmlFor={`phase-${phase.key}`}>
                  {phase.label}
                </FieldLabel>
                <Controller
                  control={control}
                  name={`phase_${phase.key}_completed_at` as PhaseField}
                  render={({ field }) => (
                    <DatePicker
                      id={`phase-${phase.key}`}
                      ref={field.ref}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </div>
            );
          })}

          {/* Nothing to show until a current phase is chosen — say so rather than rendering an
                  unexplained empty area. */}
          {!currentPhase && (
            <p className="self-center text-xs text-text-secondary @2xl:col-span-2">
              {msg.errors.selectCurrentPhaseFirst}
            </p>
          )}
        </div>

        {/* Always mounted so screen readers pick up the polite announcement when a date becomes
            kept; text-only (no colour-only meaning). */}
        <div
          role="status"
          data-testid="phase-dates-kept"
          className="space-y-1 text-xs text-text-secondary empty:hidden"
        >
          {keptPhaseDates.map((phase) => (
            <p key={phase.key}>
              {msg.errors.phaseDateKept(
                phase.label,
                fmtCalendarDate(ownPersisted[phase.key] as string),
              )}
            </p>
          ))}
        </div>
      </section>

      <Separator />

      {/* ── Narrative Fields ── */}
      <section className="space-y-4">
        <h4 className="text-sub-heading text-text-primary">
          {msg.headings.narrative}
        </h4>

        <div className="space-y-1.5">
          <FieldLabel htmlFor="project-health">
            {msg.fields.projectHealth}
          </FieldLabel>
          <Textarea
            id="project-health"
            rows={3}
            placeholder={msg.placeholders.projectHealth}
            {...register("project_health")}
          />
        </div>

        <div className="space-y-1.5">
          <FieldLabel htmlFor="planned-vs-actual">
            {msg.fields.plannedVsActual}
          </FieldLabel>
          <Textarea
            id="planned-vs-actual"
            rows={3}
            placeholder={msg.placeholders.plannedVsActual}
            {...register("planned_vs_actual")}
          />
        </div>

        <div className="space-y-1.5">
          <FieldLabel htmlFor="team-did">
            {msg.fields.teamDidThisWeek}
          </FieldLabel>
          <Textarea
            id="team-did"
            rows={3}
            placeholder={msg.placeholders.teamDidThisWeek}
            {...register("team_did_this_week")}
          />
        </div>

        <div className="space-y-1.5">
          <FieldLabel htmlFor="team-will-do">
            {msg.fields.teamWillDoNext}
          </FieldLabel>
          <Textarea
            id="team-will-do"
            rows={3}
            placeholder={msg.placeholders.teamWillDoNext}
            {...register("team_will_do_next")}
          />
        </div>

        <div className="space-y-1.5">
          <FieldLabel htmlFor="risks">{msg.fields.risks}</FieldLabel>
          <Textarea
            id="risks"
            rows={3}
            placeholder={msg.placeholders.risks}
            {...register("risks")}
          />
        </div>

        <div className="space-y-1.5">
          <FieldLabel htmlFor="decisions">{msg.fields.decisions}</FieldLabel>
          <Textarea
            id="decisions"
            rows={3}
            placeholder={msg.placeholders.decisions}
            {...register("decisions")}
          />
        </div>
      </section>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        {/* Draft is offered for a new report or an existing draft — never for a submitted one
            (the API rejects submitted → draft). */}
        {!editingSubmitted && (
          <>
            <p
              id="weekly-report-draft-hint"
              className="text-xs text-text-secondary sm:mr-auto"
            >
              {msg.lifecycle.draftHint}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={onSaveDraft}
              disabled={isPending}
              aria-describedby="weekly-report-draft-hint"
            >
              {msg.actions.saveDraft}
            </Button>
          </>
        )}
        <Button type="submit" disabled={isPending} aria-busy={isPending}>
          {isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
          {editingSubmitted ? msg.actions.resubmitForm : msg.actions.submitForm}
        </Button>
      </div>
    </form>
  );
}

// ─── Current snapshot (most recent report) ───────────────────────────────────

function ReportSnapshot({ report }: { report: WeeklyReport }): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <h3 className="flex items-center justify-between text-section-heading text-text-primary">
          <span className="flex items-center gap-2">
            <CalendarDays
              className="h-4 w-4 text-text-secondary"
              aria-hidden="true"
            />
            {msg.headings.snapshot} — {msg.snapshotFields.weekEnding}{" "}
            {fmtDate(report.week_ending)}
          </span>
          <OverallStatusBadge status={report.overall_status} />
        </h3>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* RAG grid */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <p className="text-field-title text-text-secondary">
              {msg.fields.escalation}
            </p>
            <RagDot status={report.escalation} escalationOnly />
          </div>
          <div className="space-y-1">
            <p className="text-field-title text-text-secondary">
              {msg.fields.schedule}
            </p>
            <RagDot status={report.schedule} />
          </div>
          <div className="space-y-1">
            <p className="text-field-title text-text-secondary">
              {msg.fields.scope}
            </p>
            <RagDot status={report.scope} />
          </div>
          <div className="space-y-1">
            <p className="text-field-title text-text-secondary">
              {msg.fields.budget}
            </p>
            <RagDot status={report.budget_status} />
          </div>
        </div>

        {report.cumulative_hours != null && (
          <p className="text-sm">
            <span>{msg.snapshotFields.cumulativeHours}</span>{" "}
            {report.cumulative_hours.toLocaleString()}
          </p>
        )}

        {/* Phase completion */}
        {CASDM_PHASES.some(
          (p) => report[`phase_${p.key}_completed_at` as keyof WeeklyReport],
        ) && (
          <div className="space-y-2">
            <p className="text-sub-heading text-text-primary">
              {msg.headings.phaseCompletionSnapshot}
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {CASDM_PHASES.map((p) => {
                const val = report[
                  `phase_${p.key}_completed_at` as keyof WeeklyReport
                ] as string | null;
                if (!val) return null;
                return (
                  <div
                    key={p.key}
                    className="rounded border border-outlines bg-subtle-bg px-3 py-1.5 text-xs"
                  >
                    <span className="font-medium">{p.label}</span>
                    <br />
                    {fmtDate(val)}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <Separator />

        {/* Narrative */}
        {[
          {
            label: msg.snapshotFields.projectHealth,
            value: report.project_health,
          },
          {
            label: msg.snapshotFields.plannedVsActual,
            value: report.planned_vs_actual,
          },
          {
            label: msg.snapshotFields.teamDidThisWeek,
            value: report.team_did_this_week,
          },
          {
            label: msg.snapshotFields.teamWillDoNext,
            value: report.team_will_do_next,
          },
          { label: msg.snapshotFields.risks, value: report.risks },
          { label: msg.snapshotFields.decisions, value: report.decisions },
        ].map(({ label, value }) =>
          value ? (
            <div key={label}>
              <p className="text-field-title text-text-secondary">{label}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-text-primary">
                {value}
              </p>
            </div>
          ) : null,
        )}

        <p className="text-xs text-text-secondary">
          {msg.lifecycle.submittedBy(
            report.submitted_by,
            fmtDate(report.submitted_at),
          )}
          {report.last_edited_by &&
            !sameUser(report.last_edited_by, report.submitted_by) && (
              <> · {msg.lifecycle.lastEditedBy(report.last_edited_by)}</>
            )}
        </p>
      </CardContent>
    </Card>
  );
}

// ─── Report list (T-27) ───────────────────────────────────────────────────────

const LIST_FILTERS: WeeklyReportListFilter[] = [
  "all",
  "pending",
  "approved",
  "changes_requested",
  "draft",
];

/**
 * Every report the viewer can see — submitted reports plus their own drafts (the API never returns
 * anyone else's draft) — as one list, newest week first, with a client-side review-status filter.
 *
 * Row behaviour is unchanged from the pre-T-27 lists:
 *   - a SUBMITTED row is an AccordionTrigger; clicking it expands the report (Edit, PMO review,
 *     full snapshot) in place.
 *   - a DRAFT row is a plain item with a "Continue editing" button — a button nested inside an
 *     AccordionTrigger button would be invalid HTML and not reliably keyboard / SR operable.
 */
function ReportList({
  projectId,
  reports,
  canEdit,
  onEdit,
  canReview = false,
  sowHours,
  projectPhase,
}: {
  projectId: string;
  reports: WeeklyReport[];
  canEdit: (r: WeeklyReport) => boolean;
  onEdit: (r: WeeklyReport) => void;
  /** T-28: leadership/admin get Approve / Request changes in each report body. */
  canReview?: boolean;
  sowHours?: number | null;
  projectPhase?: string | null;
}): JSX.Element | null {
  const [filter, setFilter] = useState<WeeklyReportListFilter>("all");
  // API order is week_ending DESC already; sort defensively so drafts and submitted interleave
  // by week. Array#sort is stable, so equal weeks keep the API's order.
  const ordered = useMemo(
    () =>
      [...reports].sort((a, b) =>
        (b.week_ending ?? "").localeCompare(a.week_ending ?? ""),
      ),
    [reports],
  );
  const visible = useMemo(
    () => ordered.filter((r) => matchesReportListFilter(r, filter)),
    [ordered, filter],
  );

  if (reports.length === 0) return null;
  return (
    <section className="space-y-3" aria-labelledby="weekly-report-list-heading">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h3
          id="weekly-report-list-heading"
          className="text-section-heading text-text-primary"
        >
          {msg.headings.history} ({reports.length})
        </h3>
        <div className="w-full space-y-1.5 sm:w-56">
          <FieldLabel htmlFor="weekly-report-list-filter">
            {listMsg.filterLabel}
          </FieldLabel>
          <Select
            id="weekly-report-list-filter"
            aria-label={listMsg.filterLabel}
            value={filter}
            onChange={(v) => setFilter((v || "all") as WeeklyReportListFilter)}
            options={LIST_FILTERS.map((f) => ({
              value: f,
              label: listMsg.filterOptions[f],
            }))}
          />
        </div>
      </div>

      {visible.length === 0 ? (
        <p
          role="status"
          className="rounded border border-outlines bg-card px-4 py-6 text-center text-sm text-text-secondary"
        >
          {listMsg.emptyFilter}
        </p>
      ) : (
        <Accordion type="single" collapsible className="space-y-2">
          {visible.map((r) => {
            const week = fmtDate(r.week_ending);
            if (isDraftReport(r)) {
              return (
                <div
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded border border-outlines bg-card px-4 py-3"
                >
                  <WeeklyReportRowSummary
                    report={r}
                    sowHours={sowHours}
                    projectPhase={projectPhase}
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    {r.updated_at && (
                      <span className="text-xs text-text-secondary">
                        {msg.lifecycle.lastSaved(fmtDate(r.updated_at))}
                      </span>
                    )}
                    {canEdit(r) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit(r)}
                        aria-label={msg.lifecycle.continueEditingAria(week)}
                      >
                        {msg.actions.continueEditing}
                      </Button>
                    )}
                  </div>
                </div>
              );
            }
            return (
              <AccordionItem
                key={r.id}
                value={r.id}
                className="rounded border border-outlines bg-card px-4"
              >
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex min-w-0 flex-1 pr-3">
                    <WeeklyReportRowSummary
                      report={r}
                      sowHours={sowHours}
                      projectPhase={projectPhase}
                    />
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {/* Edit lives in the panel body, not the trigger: a button inside the
                      AccordionTrigger button would be invalid, unreachable markup. */}
                  {canEdit(r) && (
                    <div className="mb-3 flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit(r)}
                        aria-label={msg.lifecycle.editAria(week)}
                      >
                        {msg.actions.edit}
                      </Button>
                    </div>
                  )}
                  <WeeklyReportReview
                    projectId={projectId}
                    report={r}
                    canReview={canReview}
                  />
                  <ReportSnapshot report={r} />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </section>
  );
}

// ─── Create-report side panel ─────────────────────────────────────────────────

/**
 * CreateReportPanel — the create-report form in a right-hand slide-in side panel.
 *
 * Uses the design-system `Sheet` (components/ui/sheet.tsx) rather than a centred `Dialog`.
 * This is a container change only: `SubmitReportForm` is rendered unchanged, so all validation,
 * field ids (the API contract) and submit behaviour are preserved.
 *
 * Sizing: the shared right-side default (`sm:max-w-xl`) is too narrow for this multi-column form,
 * so this panel alone is widened to `sm:max-w-3xl` and the body scrolls (`overflow-y-auto`) — the
 * form is taller than the viewport on most screens.
 *
 * `Sheet` is built on the same Radix dialog primitive as the old modal, so focus trapping,
 * Escape-to-close, overlay click-to-close and `aria-modal` semantics are unchanged.
 */
function CreateReportPanel({
  projectId,
  open,
  onOpenChange,
  report,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** V047 — when set, the panel edits this report (pre-filled) instead of creating one. */
  report?: WeeklyReport | null;
}): JSX.Element {
  const title = !report
    ? msg.panel.title
    : isDraftReport(report)
      ? msg.panel.draftTitle
      : msg.panel.editTitle;
  const description = !report
    ? msg.panel.description
    : isDraftReport(report)
      ? msg.panel.draftDescription
      : msg.panel.editDescription;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // Header close-button clearance and title typography come from the shared defaults in
        // sheet.tsx. Width is overridden HERE only: the shared `sm:max-w-xl` (576px) suits
        // SourcesSheet, but this form needs `sm:max-w-3xl` (768px — wider than the old 672px
        // modal) for its 4-up RAG row and 3-up date-picker row. `cn`/tailwind-merge drops the
        // default `sm:max-w-xl` in favour of this one. The grids inside respond to the panel via
        // container queries (see SubmitReportForm), so narrower viewports still reflow cleanly.
        className="flex w-full flex-col gap-0 overflow-y-auto border-outlines bg-white sm:max-w-3xl"
        data-testid="weekly-report-panel"
      >
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        <div className="mt-6">
          {/* Keyed by report so switching between reports remounts the form with fresh
              defaultValues (RHF reads defaultValues only on mount). */}
          <SubmitReportForm
            key={report?.id ?? "new"}
            projectId={projectId}
            report={report ?? undefined}
            onSuccess={() => onOpenChange(false)}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Section root ─────────────────────────────────────────────────────────────

/**
 * @param overviewMode - when true, shows the latest report snapshot + a "Create Weekly Report"
 *                       button that opens the form in a right-hand side panel. Used in the
 *                       Overview tab. The full form + history lives in the "Weekly Reports" tab.
 */
function WeeklyReportSection({
  projectId,
  overviewMode = false,
}: {
  projectId: string;
  overviewMode?: boolean;
}): JSX.Element {
  const { user } = useAuth();
  const [panelOpen, setPanelOpen] = useState(false);
  /** The report the panel is editing; null → creating a new one. */
  const [editing, setEditing] = useState<WeeklyReport | null>(null);

  const {
    data: reports,
    isLoading,
    isError,
    refetch,
  } = useWeeklyReports(projectId);

  // T-27 list columns: SOW hours and the fallback phase come from the project record. Same
  // react-query key ("project", id) the project page already loaded — no extra request there.
  const { data: project } = useProject(projectId) as { data?: Project };
  // Create/edit are member-only server-side (404 to non-members); leadership/admin bypass. False
  // while the project is still loading.
  const canSubmit = canActAsMember(user?.role, REPORT_WRITE_ROLES, project);

  // Drafts are unpublished: the "current status" snapshot reads submitted reports only. The full
  // list shows the viewer's own drafts too (the API never returns anyone else's draft).
  const submitted = useMemo(() => submittedReportsOnly(reports), [reports]);
  const [mostRecent] = submitted;

  const canEdit = (r: WeeklyReport): boolean =>
    canEditReport(r, user, project);
  const openCreate = (): void => {
    setEditing(null);
    setPanelOpen(true);
  };
  const openEdit = (r: WeeklyReport): void => {
    setEditing(r);
    setPanelOpen(true);
  };
  const onPanelOpenChange = (open: boolean): void => {
    setPanelOpen(open);
    if (!open) setEditing(null);
  };

  // ── Overview mode: snapshot + create button ───────────────────────────────
  if (overviewMode) {
    if (isLoading) {
      return <Skeleton className="h-40 w-full rounded-lg" />;
    }
    if (isError) return <></>;

    return (
      <div className="space-y-4">
        {/* Header row with create button */}
        <div className="flex items-center justify-between">
          <h3 className="text-section-heading text-text-primary">
            {msg.headings.section}
          </h3>
          {canSubmit && (
            <>
              <Button size="sm" onClick={openCreate}>
                <TrendingUp className="h-4 w-4" aria-hidden="true" />
                {msg.actions.createReport}
              </Button>

              <CreateReportPanel
                projectId={projectId}
                open={panelOpen}
                onOpenChange={onPanelOpenChange}
                report={editing}
              />
            </>
          )}
        </div>

        {/* Latest snapshot or empty state */}
        {mostRecent ? (
          <>
            {mostRecent.review_status && (
              <div className="flex justify-end">
                <ReviewStatusBadge status={mostRecent.review_status} />
              </div>
            )}
            <ReportSnapshot report={mostRecent} />
          </>
        ) : (
          <Card>
            <CardContent className="py-6 text-center text-sm text-text-secondary">
              {msg.status.emptyOverview}{" "}
              {canSubmit && <span>{msg.status.emptyOverviewHint}</span>}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ── Full mode: create button (side panel) + full history list ────────────
  return (
    <div className="space-y-6">
      {/* Create button — opens the side panel */}
      {canSubmit && (
        <div className="flex justify-end">
          <Button onClick={openCreate}>
            <TrendingUp className="h-4 w-4" aria-hidden="true" />
            {msg.actions.createReport}
          </Button>
          <CreateReportPanel
            projectId={projectId}
            open={panelOpen}
            onOpenChange={onPanelOpenChange}
            report={editing}
          />
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <Alert variant="destructive">
          <AlertTitle>{msg.status.loadFailureTitle}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>{msg.status.loadFailureBody}</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {msg.actions.retry}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* No reports yet */}
      {!isLoading && !isError && (reports ?? []).length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-text-secondary">
            {msg.status.emptyFull}
          </CardContent>
        </Card>
      )}

      {/* T-27: one list — submitted reports + the viewer's own drafts, newest week first, with a
          review-status filter. Submitted rows expand in place; drafts offer "Continue editing". */}
      {!isLoading && !isError && (
        <ReportList
          projectId={projectId}
          reports={reports ?? []}
          canEdit={canEdit}
          onEdit={openEdit}
          canReview={canReviewReports(user?.role)}
          sowHours={project?.sow_hours}
          projectPhase={project?.current_phase}
        />
      )}
    </div>
  );
}

export default WeeklyReportSection;
